import React from "react";
import { motion } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { openRubber } from "../../lib/track";
import { ExternalLink } from "lucide-react";

// Verified May 2026:
//   Wood mulch: Home Depot Vigoro 2-cu-ft bag at $3.33 = ~$50/cu yd, reapplied every spring
//   Rubber mulch: RhinoMulch bulk 2,000 lb (2.46 cu yd) at $550 = ~$220/cu yd, lasts 20+ yr
//   Yard assumption: 5 cubic yards (typical mid-size residential at 2" depth)
// 1,201 points (0.00 → 12.00 in 0.01 steps) for cents-precision tooltip interpolation.
const costData = Array.from({ length: 1201 }, (_, i) => {
  const year = i / 100;
  return {
    year: Number(year.toFixed(2)),
    wood: Math.round((250 + 250 * year) * 100) / 100,
    rubber: 1100,
  };
});

const fmtUSD = (v) =>
  `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const wood = payload.find((p) => p.dataKey === "wood")?.value;
  const rubber = payload.find((p) => p.dataKey === "rubber")?.value;
  const y = typeof label === "number" ? label.toFixed(2) : label;
  return (
    <div className="bg-[#0F0F0F] border border-[#262626] px-3 py-2 text-xs">
      <div className="text-[#A3A3A3] mb-1">Year {y}</div>
      {wood !== undefined && (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 inline-block" style={{ background: "#737373" }} />
          <span className="text-[#E8E5DE]">
            Wood mulch cost averaged over time: {fmtUSD(wood)}
          </span>
        </div>
      )}
      {rubber !== undefined && (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 inline-block" style={{ background: "#C4451C" }} />
          <span className="text-[#E8E5DE]">Rubber mulch cost: {fmtUSD(rubber)}</span>
        </div>
      )}
    </div>
  );
};

export default function CostProblem() {
  return (
    <section
      data-testid="cost-section"
      className="relative w-full py-28 sm:py-36 px-6 bg-[#0F0F0F] border-t border-[#1A1A1A]"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.0 }}
          className="mb-14 max-w-3xl"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#C4451C] mb-5">
            the case, part two
          </p>
          <h2 className="font-marker text-[#E8E5DE] text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
            The wood mulch cycle
            <br />
            is a subscription
            <br />
            <span className="text-[#C4451C]">you never signed up for.</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.0 }}
          className="bg-[#141414] border border-[#262626] p-6 sm:p-10 mb-10"
          data-testid="cost-chart-wrapper"
        >
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <h3 className="font-marker text-2xl sm:text-3xl text-[#E8E5DE]">
                12-year cumulative cost.
              </h3>
              <p className="text-sm text-[#A3A3A3] mt-1">
                Per typical residential yard (~5 cubic yards of coverage). Based on Home
                Depot Vigoro retail pricing and RhinoMulch bulk pricing.
              </p>
            </div>
            <div className="flex gap-6 text-xs">
              <div className="flex items-center gap-2 text-[#A3A3A3]">
                <span className="w-3 h-[2px] inline-block bg-[#737373]" /> Wood mulch
              </div>
              <div className="flex items-center gap-2 text-[#E8E5DE]">
                <span className="w-3 h-[2px] inline-block bg-[#C4451C]" /> Rubber mulch
              </div>
            </div>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={costData} margin={{ top: 10, right: 10, left: -5, bottom: 0 }}>
                <CartesianGrid stroke="#262626" vertical={false} />
                <XAxis
                  dataKey="year"
                  type="number"
                  domain={[0, 12]}
                  ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                  tickFormatter={(v) => `${Math.round(v)}`}
                  tick={{ fill: "#A3A3A3", fontSize: 12 }}
                  axisLine={{ stroke: "#262626" }}
                  tickLine={false}
                  label={{ value: "Year", position: "insideBottom", offset: -5, fill: "#525252", fontSize: 11 }}
                />
                <YAxis
                  domain={[0, 3500]}
                  ticks={[0, 500, 1000, 1500, 2000, 2500, 3000, 3500]}
                  tick={{ fill: "#737373", fontSize: 11 }}
                  axisLine={{ stroke: "#262626" }}
                  tickLine={false}
                  tickFormatter={(v) => `$${v.toLocaleString()}`}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ stroke: "#C4451C", strokeWidth: 1, strokeDasharray: "4 4" }}
                />
                <Line
                  type="linear"
                  dataKey="wood"
                  stroke="#737373"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, fill: "#737373", stroke: "#0F0F0F", strokeWidth: 2 }}
                  isAnimationActive={false}
                />
                <Line
                  type="linear"
                  dataKey="rubber"
                  stroke="#C4451C"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: "#C4451C", stroke: "#0F0F0F", strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-[#525252] mt-4 tracking-wider leading-relaxed">
            Wood mulch cost: 5 cu yd × $50/cu yd × annual spring reapplication, based on{" "}
            <a
              href="https://www.homedepot.com/p/Vigoro-2-cu-ft-Brown-Wood-Mulch-52050178/202089096"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#C4451C]"
            >
              <em>Home Depot Vigoro 2-cu-ft bag retail pricing</em>
            </a>
            . Rubber mulch cost: one-time purchase of 5 cu yd at $220/cu yd, based on{" "}
            <a
              href="https://recycledrubber.com/product/rhinomulch-landscape-rubber-mulch/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#C4451C]"
            >
              <em>RhinoMulch Landscape Rubber Mulch bulk pricing</em>
            </a>
            , backed by a 12-year color warranty. Material is non-biodegradable and lasts
            20+ years in practice.
          </p>
        </motion.div>

        {/* Cost breakdown table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1.0 }}
          className="overflow-hidden border border-[#262626]"
          data-testid="cost-table"
        >
          <table className="w-full text-sm sm:text-base">
            <thead>
              <tr className="bg-[#141414]">
                <th className="text-left p-4 text-[11px] tracking-[0.25em] uppercase text-[#737373] font-normal">&nbsp;</th>
                <th className="text-left p-4 text-[11px] tracking-[0.25em] uppercase text-[#A3A3A3] font-normal">Wood mulch</th>
                <th className="text-left p-4 text-[11px] tracking-[0.25em] uppercase text-[#C4451C] font-normal">Rubber mulch</th>
              </tr>
            </thead>
            <tbody className="text-[#E8E5DE]">
              {[
                ["Lifespan", "1 year", "20+ years (12-yr color warranty)"],
                ["Replacement cost (12 yr)", "~$3,250 / 5 cu yd", "~$1,100 / 5 cu yd (one-time)"],
                ["Labor / hauling", "Recurring", "One time"],
                ["Color fade", "Months", "Years"],
              ].map(([k, w, r], i) => (
                <tr key={i} className="border-t border-[#1F1F1F]">
                  <td className="p-4 text-[#A3A3A3]">{k}</td>
                  <td className="p-4 text-[#A3A3A3]">{w}</td>
                  <td className="p-4 text-[#E8E5DE]">{r}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        <div className="mt-10 text-center">
          <button
            onClick={() => openRubber("cost-shop-link", "cost", "https://recycledrubber.com/")}
            data-testid="cost-shop-link"
            className="text-xs tracking-[0.3em] uppercase text-[#C4451C] hover:text-[#E8E5DE] inline-flex items-center gap-2"
          >
            Shop rubber mulch <ExternalLink size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
